import {
  Button,
  Card,
  Drawer,
  Dropdown,
  Flex,
  Layout,
  Modal,
  Tooltip,
  Typography,
} from 'antd';
import './index.scss';
import { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import AGBLogo from 'assets/images/logoAGB.svg';
import urls from 'routes/urls';
import {
  LogoutOutlined,
  ReloadOutlined,
  InfoCircleOutlined,
  DownOutlined,
  LockOutlined,
  MenuOutlined,
} from '@ant-design/icons';

import { useLeftNavBarItems } from './LeftNavBarItems';
import useAccountAccess from 'hooks/useAccountAccess';
import { useLanguage } from 'hooks/Language/useLanguage';
import NavbarMenu from './NavbarMenu';
import { MenuItem } from 'components/shared/LocalMenu/menuItems';
import { screenLayoutConfigurations } from 'assets/customStyle/GlobalLayoutStyle';
import useMediaQuery, {
  useScreenStyle,
  ScreenType,
} from 'hooks/styleMediaQuery/useMediaQuery';
import useLogout from 'hooks/useLogout';
import agbColors from 'constants/Colors/agbColors';
import { ItemType } from 'antd/es/menu/interface';
import { Header } from 'antd/es/layout/layout';
import BKHeaderContent from '../HeaderContent';
import { menuItems as dropDownItems } from 'pages/bank/Accounts';
import TransferToMNOPageContainer from 'containers/bank/AGBBank/QuickTransfer/TransferToMNO';
import BetweenOwnAccountContainer from 'containers/bank/AGBBank/Transfer/BetweenOwnAccount';
import InternationalTransferContainer from 'containers/bank/AGBBank/Transfer/InternationalTransfer';
import LocalTransferContainer from 'containers/bank/AGBBank/Transfer/LocalTransfer';
import { getUserData } from 'queries/ossix/user/userData';
import { IUserData } from 'interfaces/queries/userData';
import SessionTimeoutCountdown from './CountDown/CountDown';

const { Content } = Layout;

const { Title, Paragraph } = Typography;

type PageTitleType =
  | 'Overview'
  | 'Accounts'
  | 'Transactions'
  | 'Beneficiaries'
  | 'Microloan'
  | 'Cards';

type CustomMenuItem = MenuItem & {
  label: {
    props: {
      to: string;
    };
  };
};

const contentBoxPadding =
  'small-padding-left small-padding-right small-padding-bottom small-padding-top';

function isCustomMenuItem(item: ItemType): item is CustomMenuItem {
  return (item as CustomMenuItem).label !== undefined;
}

interface BKGlobalLayoutProps {
  width?: string | number;
  height?: string | number;
  cardTitle?: string;
  children?: React.ReactNode;
  isInputForm?: boolean;
  isBeneficiaryComponent?: boolean;
  componentBox?: React.ReactNode;
  cover?: React.ReactNode;
  isCustomComponent?: boolean;
  className?: string;
  formSize?: string | number;
}

const BKGlobalLayout: React.FC<BKGlobalLayoutProps> = ({
  children,
  cardTitle,
  isInputForm = false,
  componentBox,
  width = '40%',
  formSize = '50%',
  cover,
  isBeneficiaryComponent = false,
  isCustomComponent = false,
  className = '',
  height = '80vh',
}) => {
  const { translate } = useLanguage();
  const { pathname } = useLocation();

  const { onHandleLogout, loadLogoutUser } = useLogout();
  const [openDrawer1, setOpenDrawer1] = useState(false);
  const [pageTitle, setPageTitle] = useState<PageTitleType | string>(
    'Dashboard',
  );

  const [isDrawerOpen, setIsDrawerOpen] = useState({
    drawerTitle: '',
    open: false,
  });

  const { isLoading: isLoadingUserData, userData } = getUserData();
  const userInformation: IUserData = userData && userData[0];

  const { isPending: isAccountPending } = useAccountAccess();

  const menuItems = useLeftNavBarItems(
    userInformation?.PartnerOrganization === 'YES' ? true : false,
    userInformation?.CashPointLogedIn,
    isAccountPending,
  );

  const formatTitle = pathname.split('/')[1]?.toUpperCase();
  const currentPage: PageTitleType | string =
    formatTitle === 'Dashboard'
      ? 'Overview'
      : formatTitle || pageTitle;

  const screen = useMediaQuery();
  const screenSize = screen as ScreenType;
  const style = useScreenStyle(
    screenSize,
    screenLayoutConfigurations,
  );

  const handleActivity = () => setLastActiveTime(Date.now());
  const [lastActiveTime, setLastActiveTime] = useState(Date.now());
  const [showWarningModal, setShowWarningModal] = useState(false);

  const isInactive = (timeoutDuration: number): boolean => {
    const now = Date.now();
    return now - lastActiveTime > timeoutDuration;
  };

  const resetTimeout = () => {
    setLastActiveTime(Date.now());
    setShowWarningModal(false);
  };

  const handleCloseOpenDrawer = () =>
    setIsDrawerOpen({
      drawerTitle: '',
      open: false,
    });

  const handleOpenDrawer = (drawerTitle: string) =>
    setIsDrawerOpen({
      open: true,
      drawerTitle: drawerTitle,
    });

  useEffect(() => {
    setPageTitle(currentPage);
  }, [currentPage]);

  const getSelectedKey = (): string => {
    const path = location.pathname;
    const item: any = menuItems.find(
      (item) =>
        isCustomMenuItem(item) &&
        path.startsWith(item.label.props.to),
    );
    return item ? item.key.toString() : '1';
  };

  useEffect(() => {
    const timeoutDuration: number = 1000 * 60 * 5;
    const alertTimeout: number = 1000 * 60;

    const handleTimeout = () => {
      if (isInactive(timeoutDuration)) {
        if (showWarningModal) {
          onHandleLogout();
        } else {
          setShowWarningModal(true);
          setTimeout(() => {
            if (showWarningModal) {
              setShowWarningModal(false);
              onHandleLogout();
            }
          }, alertTimeout);
        }
      }
    };

    const timeoutId = setTimeout(handleTimeout, timeoutDuration);

    return () => clearTimeout(timeoutId);
  }, [lastActiveTime, showWarningModal]);

  useEffect(() => {
    window.addEventListener('mousemove', handleActivity);
    window.addEventListener('keydown', handleActivity);

    return () => {
      window.removeEventListener('mousemove', handleActivity);
      window.removeEventListener('keydown', handleActivity);
    };
  }, []);

  const onHandleOpenDrawer = () => {
    setOpenDrawer1(true);
  };
  const onHandleCloseDrawer = () => setOpenDrawer1(false);

  const renderContent = (
    formWidth?: string | number,
    width?: string | number,
  ) => {
    switch (true) {
      case isBeneficiaryComponent:
        return (
          <Card
            cover={cover}
            size="small"
            title={cardTitle ?? ''}
            style={{ width: width, height: height }}
            className={`align-self-center ${className}`}
            key="beneficiary"
          >
            {componentBox}
          </Card>
        );
      case isCustomComponent:
        return (
          <Flex
            gap="small"
            align="center"
            justify="center"
            style={{
              width: width,
              height: height,
            }}
            className={`align-self-center ${className} responsive-custom-component`}
            key="custom"
          >
            {componentBox}
          </Flex>
        );
      case isInputForm:
        return (
          <div
            style={{
              width: formWidth,
              height: height,
            }}
            className={`align-self-center ${className} manage-scroll-bar`}
            key="inputForm"
          >
            {componentBox}
          </div>
        );
      default:
        return (
          <Flex
            align="center"
            justify="center"
            style={{ width: '100%' }}
          >
            {children}
          </Flex>
        );
    }
  };

  return (
    <Layout className="global-layout-container">
      {!openDrawer1 ? (
        <NavbarMenu
          logo={AGBLogo}
          url={urls.dashboard}
          keys={getSelectedKey()}
          items={menuItems}
          className="global-left-container"
        />
      ) : (
        <Drawer
          placement="left"
          closable={false}
          onClose={onHandleCloseDrawer}
          open={openDrawer1}
        >
          <NavbarMenu
            logo={AGBLogo}
            url={urls.dashboard}
            keys={getSelectedKey()}
            items={menuItems}
            isMobileView
            onClose={onHandleCloseDrawer}
            className="mobile-view"
          />
        </Drawer>
      )}

      <div className="global-right-layout-box">
        <Header className="global-layout__top-container xlarge-padding-left xlarge-padding-right">
          <div className="background-pattern">
            <div className="line-pattern"></div>
          </div>
          <div className="top-container__right">
            <div className="dashboard-user-profile">
              {isAccountPending ? (
                // Nothing to transfer from until the account is opened.
                <Tooltip
                  title={translate('Unlocks once your account is opened')}
                  placement="bottomLeft"
                >
                  <Button
                    size="large"
                    className="transfer-btn--locked"
                    icon={<LockOutlined />}
                  >
                    {translate('Transfer')}
                  </Button>
                </Tooltip>
              ) : (
                <Dropdown
                  overlay={dropDownItems(translate, handleOpenDrawer)}
                  placement="bottomLeft"
                >
                  <Button
                    size="large"
                    variant="filled"
                    style={{
                      background: agbColors.DarkGoldenrodColor,
                      color: agbColors.whiteColor,
                      border: 'none',
                    }}
                  >
                    {translate('Transfer')}
                    <DownOutlined />
                  </Button>
                </Dropdown>
              )}
            </div>

            <Button
              type="text"
              className="menu-icon__btn"
              onClick={onHandleOpenDrawer}
            >
              <MenuOutlined />
            </Button>

            <BKHeaderContent
              profilePicture={
                !isLoadingUserData && userInformation?.PictureURL
              }
              lastName={
                !isLoadingUserData && userInformation?.LastName
              }
              firstName={
                !isLoadingUserData && userInformation?.FirstName
              }
            />
          </div>
        </Header>

        <Layout className="global-layout__bottom-container">
          <Content className="content-layout">
            <Flex
              vertical
              {...style.card}
              className={
                screenSize === 'mobile' || screenSize === 'tablet'
                  ? ''
                  : contentBoxPadding
              }
            >
              {renderContent(formSize, width)}
            </Flex>
          </Content>
        </Layout>

        {isDrawerOpen.drawerTitle.toLowerCase() ===
          'AGB TO AGB'.toLowerCase() && (
          <InternationalTransferContainer
            isDrawerOpen={isDrawerOpen}
            handleCloseOpenDrawer={handleCloseOpenDrawer}
          />
        )}
        {isDrawerOpen.drawerTitle.toLowerCase() ===
          'Operators'.toLowerCase() && (
          <TransferToMNOPageContainer
            isDrawerOpen={isDrawerOpen}
            handleCloseOpenDrawer={handleCloseOpenDrawer}
          />
        )}
        {isDrawerOpen.drawerTitle.toLowerCase() ===
          'Between own accounts'.toLowerCase() && (
          <BetweenOwnAccountContainer
            isDrawerOpen={isDrawerOpen}
            handleCloseOpenDrawer={handleCloseOpenDrawer}
          />
        )}

        {isDrawerOpen.drawerTitle.toLowerCase() ===
          'To Another Bank Account'.toLowerCase() && (
          // <LocalTransferContainer
          //   isDrawerOpen={isDrawerOpen}
          //   handleCloseOpenDrawer={handleCloseOpenDrawer}
          // />
          <InternationalTransferContainer
            isDrawerOpen={isDrawerOpen}
            handleCloseOpenDrawer={handleCloseOpenDrawer}
          />
        )}
      </div>

      <Modal
        open={showWarningModal}
        closable={false}
        className="timeout-warning-modal"
        width={420}
        centered
        maskStyle={{ backgroundColor: 'rgba(0, 0, 0, 0.65)' }}
        bodyStyle={{ padding: '32px 24px 24px' }}
        footer={
          <Flex gap="middle" align="center" justify="space-between">
            <Button
              danger
              size="large"
              loading={loadLogoutUser}
              onClick={onHandleLogout}
              icon={<LogoutOutlined />}
              style={{ flex: 1 }}
            >
              {translate('Log Out')}
            </Button>
            <Button
              type="primary"
              size="large"
              onClick={resetTimeout}
              icon={<ReloadOutlined />}
              style={{ flex: 1 }}
            >
              {translate('Stay Logged In')}
            </Button>
          </Flex>
        }
      >
        <Flex vertical align="center" gap="middle">
          <div className="warning-icon-container">
            <InfoCircleOutlined className="warning-icon" />
          </div>
          <Title level={4} style={{ margin: 0, textAlign: 'center' }}>
            {translate('Session Timeout Warning')}
          </Title>
          <Paragraph style={{ textAlign: 'center', marginBottom: 0 }}>
            {translate(
              'You have been inactive for a while. Your session will time out in',
            )}{' '}
            <SessionTimeoutCountdown
              initialMinutes={5}
              onExpire={() => {
                // Add logic Here
              }}
            />{' '}
            {translate('minutes')}
          </Paragraph>
        </Flex>
      </Modal>
    </Layout>
  );
};

export default BKGlobalLayout;
